import type { Product, VariantGroup } from '../types/product';
import { IMG } from './images';

const installGroup: VariantGroup = {
  id: 'install',
  name: 'Installatie',
  kind: 'card',
  options: [
    { id: 'self', label: 'Ik heb een eigen installateur', detail: 'Thuisbezorgd, klaar voor montage', priceDelta: 0 },
    { id: 'pro', label: 'Gecertificeerde Ampra-installateur', detail: 'Geplaatst en in bedrijf binnen 3 weken', priceDelta: 749 },
  ],
};

export const products: Product[] = [
  // ——— Thuisbatterijen (configurator)
  {
    id: 'ampra-stack',
    name: 'Ampra Stack Thuisbatterij',
    brand: 'Ampra',
    category: 'home-batteries',
    template: 'system',
    image: IMG.home,
    gallery: [IMG.home, IMG.homeDark, IMG.hero],
    price: 2649,
    compareAt: 2949,
    rating: 4.9,
    reviews: 412,
    specs: ['5,1–15,3 kWh', 'Modulair'],
    badge: 'Bestseller',
    stock: 'in-stock',
    capacityWh: 5100,
    summary:
      'Een modulaire LFP-thuisbatterij die met je meegroeit. Begin met twee modules en breid later uit — elke module klik je er in een paar minuten bij, zonder extra bekabeling.',
    highlights: ['LiFePO₄-cellen, 8.000 cycli', 'Uitbreidbaar van 5,1 tot 15,3 kWh', 'Noodstroom binnen 10 ms', '10 jaar garantie'],
    specTable: [
      { label: 'Celtype', value: 'LiFePO₄ (LFP)' },
      { label: 'Capaciteit per module', value: '2,56 kWh' },
      { label: 'Max. aantal modules', value: '6' },
      { label: 'Levensduur', value: '8.000 cycli bij 90% DoD' },
      { label: 'IP-klasse', value: 'IP65 (binnen & buiten)' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capaciteit',
        kind: 'card',
        options: [
          { id: '5', label: '5,1 kWh', detail: '2 modules · kleine woning', priceDelta: 0, capacityWh: 5100 },
          { id: '7', label: '7,7 kWh', detail: '3 modules · 2–3 personen', priceDelta: 700, capacityWh: 7700 },
          { id: '10', label: '10,2 kWh', detail: '4 modules · meest gekozen', priceDelta: 1350, capacityWh: 10200 },
          { id: '15', label: '15,3 kWh', detail: '6 modules · warmtepomp / EV', priceDelta: 2650, capacityWh: 15300 },
        ],
      },
      {
        id: 'color',
        name: 'Afwerking',
        kind: 'swatch',
        options: [
          { id: 'white', label: 'Arctic wit', swatch: '#EEF1F0', priceDelta: 0, image: IMG.home },
          { id: 'anthracite', label: 'Antraciet', swatch: '#2E3438', priceDelta: 0, image: IMG.homeDark },
        ],
      },
      {
        id: 'inverter',
        name: 'Hybride omvormer',
        kind: 'card',
        options: [
          { id: 'none', label: 'Mijn bestaande omvormer', detail: 'Compatibele hybride omvormer vereist', priceDelta: 0 },
          { id: 'h5', label: 'Ampra Hybrid 5 kW', detail: 'Tot 12 panelen', priceDelta: 1190 },
          { id: 'h8', label: 'Ampra Hybrid 8 kW', detail: 'Tot 20 panelen, 3-fase', priceDelta: 1590 },
        ],
      },
      installGroup,
    ],
    bundleIds: ['homewizard-p1', 'mc4-kit'],
  },
  {
    id: 'alphaess-vitapower',
    name: 'AlphaESS VitaPower Thuisbatterij',
    brand: 'AlphaESS',
    category: 'home-batteries',
    template: 'system',
    image: IMG.homeDark,
    gallery: [IMG.homeDark, IMG.home],
    price: 2149,
    rating: 4.7,
    reviews: 96,
    specs: ['8–16 kWh', '3,68 kW'],
    stock: 'in-stock',
    capacityWh: 8000,
    summary: 'Plug-and-play thuisopslag met geïntegreerde omvormer. Sluit aan op een gewoon stopcontact — ideaal voor bestaande installaties.',
    highlights: ['Geïntegreerde 3,68 kW omvormer', 'IP65 binnen/buiten', 'App met dynamische tarieven', '10 jaar garantie'],
    specTable: [
      { label: 'Celtype', value: 'LiFePO₄ (LFP)' },
      { label: 'Continu vermogen', value: '3.680 W' },
      { label: 'IP-klasse', value: 'IP65' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capaciteit',
        kind: 'card',
        options: [
          { id: '8', label: '8 kWh', detail: '2 modules', priceDelta: 0, capacityWh: 8000 },
          { id: '12', label: '12 kWh', detail: '3 modules', priceDelta: 990, capacityWh: 12000 },
          { id: '16', label: '16 kWh', detail: '4 modules', priceDelta: 1980, capacityWh: 16000 },
        ],
      },
      installGroup,
    ],
    bundleIds: ['homewizard-p1'],
  },
  {
    id: 'huawei-luna2000',
    name: 'Huawei LUNA2000 Slimme Batterij',
    brand: 'Huawei',
    category: 'home-batteries',
    template: 'system',
    image: IMG.home,
    gallery: [IMG.home, IMG.hero],
    price: 2390,
    compareAt: 2590,
    rating: 4.8,
    reviews: 221,
    specs: ['5–15 kWh', '2,5 kW/module'],
    stock: 'low',
    capacityWh: 5000,
    summary: 'Dé batterij voor Huawei SUN2000-omvormers. Optimalisatie per module zorgt dat elke kWh benut wordt.',
    highlights: ['Energie-optimalisatie per module', 'Werkt met SUN2000-omvormers', '10 jaar garantie'],
    specTable: [
      { label: 'Celtype', value: 'LiFePO₄ (LFP)' },
      { label: 'Capaciteit per module', value: '5 kWh' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capaciteit',
        kind: 'card',
        options: [
          { id: '5', label: '5 kWh', detail: '1 module', priceDelta: 0, capacityWh: 5000 },
          { id: '10', label: '10 kWh', detail: '2 modules', priceDelta: 1790, capacityWh: 10000 },
          { id: '15', label: '15 kWh', detail: '3 modules', priceDelta: 3580, capacityWh: 15000 },
        ],
      },
      installGroup,
    ],
  },

  // ——— Plug-in batterijen
  {
    id: 'zendure-solarflow-2400',
    name: 'Zendure SolarFlow 2400 AC',
    brand: 'Zendure',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 899,
    rating: 4.7,
    reviews: 238,
    specs: ['2,4 kWh', '2400 W'],
    badge: 'Nieuw',
    stock: 'in-stock',
    capacityWh: 2400,
    summary: 'Steek de stekker in het stopcontact en sla overtollige balkonstroom op. Laadt ’s nachts uit het net wanneer stroom het goedkoopst is.',
    highlights: ['Plug & play, geen installateur', 'Laden op dynamisch tarief', 'Uitbreidbaar tot 16,8 kWh', 'IP65'],
    specTable: [
      { label: 'Capaciteit', value: '2.400 Wh' },
      { label: 'AC-vermogen', value: '2.400 W' },
      { label: 'Zonne-ingang', value: '4 × MPPT, 3.000 W' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'expansion',
        name: 'Extra batterij',
        kind: 'pill',
        options: [
          { id: 'none', label: 'Geen', priceDelta: 0, capacityWh: 2400 },
          { id: '1', label: '+1 AB3000 (2,9 kWh)', priceDelta: 699, capacityWh: 5280 },
          { id: '2', label: '+2 AB3000 (5,8 kWh)', priceDelta: 1349, capacityWh: 8160 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460', 'homewizard-p1'],
  },
  {
    id: 'anker-solarbank-3',
    name: 'Anker SOLIX Solarbank 3 Pro',
    brand: 'Anker SOLIX',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 1099,
    compareAt: 1299,
    rating: 4.6,
    reviews: 187,
    specs: ['2,7 kWh', '1200 W'],
    stock: 'in-stock',
    capacityWh: 2700,
    summary: 'Slimme balkonopslag met AI-tariefplanning en een strak, weerbestendig design.',
    highlights: ['AI-energieplanning', 'Uitbreidbaar tot 16 kWh', 'IP65 weerbestendig'],
    specTable: [
      { label: 'Capaciteit', value: '2.688 Wh' },
      { label: 'AC-vermogen', value: '1.200 W' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'expansion',
        name: 'Extra batterij',
        kind: 'pill',
        options: [
          { id: 'none', label: 'Geen', priceDelta: 0, capacityWh: 2700 },
          { id: '1', label: '+1 BP2700', priceDelta: 749, capacityWh: 5400 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460'],
  },
  {
    id: 'ampra-plug',
    name: 'Ampra Plug 2 Balkonbatterij',
    brand: 'Ampra',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 649,
    rating: 4.5,
    reviews: 74,
    specs: ['1,9 kWh', '800 W'],
    badge: 'Nieuw',
    stock: 'in-stock',
    capacityWh: 1920,
    summary: 'De voordeligste manier om balkonstroom op te slaan. Compact, stil en in 10 minuten geïnstalleerd.',
    highlights: ['In 10 minuten klaar', 'Stil, zonder ventilator', '5 jaar garantie'],
    specTable: [
      { label: 'Capaciteit', value: '1.920 Wh' },
      { label: 'AC-vermogen', value: '800 W' },
      { label: 'Garantie', value: '5 jaar' },
    ],
    variants: [
      {
        id: 'color',
        name: 'Kleur',
        kind: 'swatch',
        options: [
          { id: 'white', label: 'Arctic wit', swatch: '#EEF1F0', priceDelta: 0 },
          { id: 'anthracite', label: 'Antraciet', swatch: '#2E3438', priceDelta: 20 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460', 'mc4-kit'],
  },

  // ——— Powerstations
  {
    id: 'ecoflow-delta-3-plus',
    name: 'EcoFlow DELTA 3 Plus Powerstation',
    brand: 'EcoFlow',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 699,
    compareAt: 899,
    rating: 4.8,
    reviews: 1024,
    specs: ['1024 Wh', '1800 W'],
    stock: 'low',
    capacityWh: 1024,
    summary: 'Snelladende draagbare stroom voor kamperen, campers en noodstroom thuis. In 56 minuten van 0 naar 100% via het stopcontact.',
    highlights: ['0–100% in 56 min', 'UPS-omschakeling < 10 ms', '13 uitgangen', 'Uitbreidbaar tot 5 kWh'],
    specTable: [
      { label: 'Capaciteit', value: '1.024 Wh' },
      { label: 'AC-vermogen', value: '1.800 W (piek 3.600 W)' },
      { label: 'Gewicht', value: '12,5 kg' },
      { label: 'Garantie', value: '5 jaar' },
    ],
    variants: [
      {
        id: 'bundle',
        name: 'Bundel',
        kind: 'pill',
        options: [
          { id: 'unit', label: 'Alleen het apparaat', priceDelta: 0, capacityWh: 1024 },
          { id: 'solar', label: '+ 220 W zonnepaneel', priceDelta: 349, capacityWh: 1024 },
          { id: 'battery', label: '+ Extra batterij', priceDelta: 599, capacityWh: 2048 },
        ],
      },
    ],
    bundleIds: ['mc4-kit', 'ecoflow-river-3'],
  },
  {
    id: 'bluetti-ac180',
    name: 'BLUETTI AC180 Draagbare Powerstation',
    brand: 'BLUETTI',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 549,
    rating: 4.6,
    reviews: 640,
    specs: ['1152 Wh', '1800 W'],
    stock: 'in-stock',
    capacityWh: 1152,
    summary: 'Betrouwbare allrounder met LFP-cellen en 3.500+ cycli. Favoriet bij kampeerders en vanlifers.',
    highlights: ['LiFePO₄, 3.500+ cycli', '1.800 W vermogen', 'Bediening via app'],
    specTable: [
      { label: 'Capaciteit', value: '1.152 Wh' },
      { label: 'AC-vermogen', value: '1.800 W' },
      { label: 'Gewicht', value: '16 kg' },
      { label: 'Garantie', value: '5 jaar' },
    ],
    variants: [
      {
        id: 'bundle',
        name: 'Bundel',
        kind: 'pill',
        options: [
          { id: 'unit', label: 'Alleen het apparaat', priceDelta: 0 },
          { id: 'solar', label: '+ 200 W zonnepaneel', priceDelta: 299 },
        ],
      },
    ],
    bundleIds: ['mc4-kit'],
  },
  {
    id: 'ecoflow-river-3',
    name: 'EcoFlow RIVER 3 Powerstation',
    brand: 'EcoFlow',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 199,
    rating: 4.5,
    reviews: 312,
    specs: ['245 Wh', '300 W'],
    badge: 'Nieuw',
    stock: 'in-stock',
    capacityWh: 245,
    summary: 'Compacte stroom voor een weekendje weg. Laadt laptops, camera’s en verlichting.',
    highlights: ['Slechts 3,5 kg', 'Stille X-Boost 600 W', 'In 1 uur volledig geladen'],
    specTable: [
      { label: 'Capaciteit', value: '245 Wh' },
      { label: 'AC-vermogen', value: '300 W' },
      { label: 'Gewicht', value: '3,5 kg' },
    ],
  },
  {
    id: 'anker-solix-c1000',
    name: 'Anker SOLIX C1000 Powerstation',
    brand: 'Anker SOLIX',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 449,
    compareAt: 599,
    rating: 4.7,
    reviews: 805,
    specs: ['1056 Wh', '1800 W'],
    stock: 'in-stock',
    capacityWh: 1056,
    summary: 'Compacte 1 kWh powerstation met UltraFast-laden en ingebouwde lamp.',
    highlights: ['Volledig opgeladen in 58 min', 'Ingebouwde kampeerlamp', '5 jaar garantie'],
    specTable: [
      { label: 'Capaciteit', value: '1.056 Wh' },
      { label: 'AC-vermogen', value: '1.800 W' },
      { label: 'Gewicht', value: '12,9 kg' },
    ],
  },

  // ——— Zonnepanelen
  {
    id: 'aiko-neostar-460',
    name: 'Aiko Neostar 460 Wp Full Black',
    brand: 'Aiko',
    category: 'solar-panels',
    template: 'standard',
    image: IMG.solar,
    gallery: [IMG.solar, IMG.pluginLife],
    price: 119,
    compareAt: 139,
    rating: 4.8,
    reviews: 352,
    specs: ['460 Wp', '23,6%'],
    badge: 'Bestseller',
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Volledig zwart back-contact paneel met toonaangevend rendement en een strakke uitstraling op elk dak.',
    highlights: ['23,6% rendement', 'Full black design', '25 jaar productgarantie'],
    specTable: [
      { label: 'Vermogen', value: '460 Wp' },
      { label: 'Rendement', value: '23,6%' },
      { label: 'Afmetingen', value: '1762 × 1134 × 30 mm' },
      { label: 'Garantie', value: '25 jaar' },
    ],
    variants: [
      {
        id: 'pack',
        name: 'Aantal',
        kind: 'pill',
        options: [
          { id: '1', label: '1 paneel', priceDelta: 0 },
          { id: '4', label: '4 panelen', detail: '€ 112 per stuk', priceDelta: 329 },
          { id: '10', label: '10 panelen', detail: '€ 105 per stuk', priceDelta: 931 },
        ],
      },
    ],
    bundleIds: ['mc4-kit'],
  },
  {
    id: 'jinko-tiger-neo-440',
    name: 'Jinko Tiger Neo 440 Wp',
    brand: 'Jinko',
    category: 'solar-panels',
    template: 'standard',
    image: IMG.solar,
    gallery: [IMG.solar],
    price: 99,
    rating: 4.6,
    reviews: 188,
    specs: ['440 Wp', '22,5%'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'N-type TOPCon-paneel met een scherpe prijs per watt en lage degradatie.',
    highlights: ['N-type TOPCon', 'Lage degradatie', '25 jaar garantie'],
    specTable: [
      { label: 'Vermogen', value: '440 Wp' },
      { label: 'Rendement', value: '22,5%' },
      { label: 'Garantie', value: '25 jaar' },
    ],
  },

  // ——— Omvormers
  {
    id: 'ampra-hybrid',
    name: 'Ampra Hybride Omvormer',
    brand: 'Ampra',
    category: 'inverters',
    template: 'standard',
    image: IMG.inverter,
    gallery: [IMG.inverter, IMG.hero],
    price: 1190,
    rating: 4.8,
    reviews: 143,
    specs: ['5–10 kW', 'Hybride'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Hybride omvormer die panelen, batterij en net in één kast regelt. Perfect te combineren met de Ampra Stack.',
    highlights: ['Batterij-ready', 'Noodstroomuitgang', 'Monitoring via wifi'],
    specTable: [
      { label: 'Fasen', value: '1 of 3' },
      { label: 'Rendement', value: '97,6%' },
      { label: 'Garantie', value: '10 jaar' },
    ],
    variants: [
      {
        id: 'power',
        name: 'Vermogen',
        kind: 'pill',
        options: [
          { id: '5', label: '5 kW', priceDelta: 0 },
          { id: '8', label: '8 kW', priceDelta: 400 },
          { id: '10', label: '10 kW', priceDelta: 790 },
        ],
      },
    ],
    bundleIds: ['homewizard-p1'],
  },
  {
    id: 'victron-multiplus-ii',
    name: 'Victron MultiPlus-II 48/5000',
    brand: 'Victron Energy',
    category: 'inverters',
    template: 'standard',
    image: IMG.inverter,
    gallery: [IMG.inverter],
    price: 1349,
    rating: 4.9,
    reviews: 67,
    specs: ['5 kVA', '48 V'],
    stock: 'preorder',
    capacityWh: 0,
    summary: 'Professionele omvormer/lader voor off-grid- en ESS-systemen.',
    highlights: ['Off-grid & ESS', 'Tot 6 units parallel', '5 jaar garantie'],
    specTable: [
      { label: 'Vermogen', value: '5.000 VA' },
      { label: 'Accuspanning', value: '48 V' },
    ],
  },

  // ——— Accessoires
  {
    id: 'homewizard-p1',
    name: 'HomeWizard P1 Energiemeter',
    brand: 'HomeWizard',
    category: 'accessories',
    template: 'standard',
    image: IMG.accessory,
    gallery: [IMG.accessory],
    price: 29.95,
    rating: 4.7,
    reviews: 1530,
    specs: ['Wifi', 'P1-poort'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Lees je slimme meter live uit. Nodig voor nul-op-de-meter bij de meeste plug-in batterijen.',
    highlights: ['Live verbruik per seconde', 'Werkt met Zendure & Anker'],
    specTable: [{ label: 'Aansluiting', value: 'P1-poort, wifi' }],
  },
  {
    id: 'mc4-kit',
    name: 'Solarkabelset met MC4',
    brand: 'Ampra',
    category: 'accessories',
    template: 'standard',
    image: IMG.accessory,
    gallery: [IMG.accessory],
    price: 24.95,
    rating: 4.6,
    reviews: 420,
    specs: ['6 mm²', 'UV-bestendig'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Voorgekrompen rode/zwarte solarkabel met MC4-connectoren.',
    highlights: ['Voorgekrompen', 'UV- en weerbestendig'],
    specTable: [{ label: 'Doorsnede', value: '6 mm²' }],
    variants: [
      {
        id: 'length',
        name: 'Lengte',
        kind: 'pill',
        options: [
          { id: '5', label: '5 m', priceDelta: 0 },
          { id: '10', label: '10 m', priceDelta: 10 },
          { id: '25', label: '25 m', priceDelta: 30 },
        ],
      },
    ],
  },
];
